import React, { useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Highlighter, Typeahead } from "react-bootstrap-typeahead";
import { useForm, Controller } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useStateMachine } from "little-state-machine";
import { useQueryClient } from "@tanstack/react-query";

import Cross from "assets/images/close.svg";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import { getApprovers, reapproveQuotation } from "api/services/quotation";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { userQueryKeys } from "api/queries/user/userQueryKeys";
import { updateBackgroundLoadingState } from "store/store";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";

const TransferApprovalDialog = function ({
  isOpen,
  close,
  openManageApprovalDialog,
  quotationId,
  shipmentId,
  setShowBookQuoteDialog,
  setBookingPayload,
  setBookingType,
  status,
}) {
  const queryClient = useQueryClient();
  const intl = useIntl();
  const typeaheadRef = useRef(null);
  const { setError, clearErrors, control, handleSubmit } = useForm({
    shouldFocusError: true,
  });
  const { permissions } = usePermissions();
  const { email: userEmail } = JSON.parse(permissions);
  const { actions } = useStateMachine({ updateBackgroundLoadingState });

  const [approverOptions, setApproverOptions] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  /** Get list of approvers if dialog is opened, reset state if closed */
  useEffect(() => {
    if (isOpen) getListOfApprovers();
    else {
      setApproverOptions([]);
      setSelectedApprovers([]);
      setComment("");
    }
  }, [isOpen]);

  /** [GET] Gets a list of users that can be requested approval from */
  const getListOfApprovers = () => {
    setIsLoading(true);
    const query = "/list-approvers?include_same_level=true";
    getApprovers(quotationId, query)
      .then((res) => {
        const allApprovers = res.data.users.map(({ id, name, email, is_preapprover, is_staged, is_invited }) => ({
          id,
          label: name,
          email,
          is_preapprover,
          is_staged,
          is_invited,
        }));
        const availableApprovers = allApprovers.filter(({ email, is_invited }) => email !== userEmail && !is_invited);
        const invitedApprovers = allApprovers.filter(({ is_invited }) => is_invited);
        setApproverOptions(availableApprovers);
        setSelectedApprovers(invitedApprovers);
      })
      .catch((err) => errorToast(err, "Could not retrieve list of approvers. Please try again later."))
      .finally(() => setIsLoading(false));
  };

  /** [POST] Transfer the approval request */
  const transferApproval = () => {
    if (selectedApprovers?.length === 0) {
      setError("selected_approvers", { type: "invalid", message: "Please select a user" });
      return;
    }

    const payload = {
      approval_request_to: selectedApprovers.filter(({ is_invited }) => !is_invited).map(({ id }) => id),
      approval_comment: comment,
      add_to_existing: true,
    };

    if (status === "reapproval_needed_after_booking") {
      confirmTransferReapproval(payload);
    } else {
      setBookingPayload(payload);
      setBookingType("transfer_approval");
      close();
      setShowBookQuoteDialog(true);
    }
  };

  /** Close this dialog and open manage approval dialog */
  const handleManageApprovalClick = () => {
    close();
    openManageApprovalDialog();
  };

  const confirmTransferReapproval = (payload) => {
    setIsApproving(true);
    actions.updateBackgroundLoadingState(true);
    reapproveQuotation(quotationId, payload)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: userQueryKeys.userPendingTasks });
        const toastMessage = (
          <FormattedMessage
            id="shipment-quotes.components.bookQuoteDialog.approvalRequested"
            defaultMessage="Approval requested"
          />
        );
        toast(toastMessage, {
          type: "success",
        });
        queryClient.invalidateQueries({ queryKey: quoteRequestQueryKeys.quoteQuotationListing(shipmentId) });
        close();
      })
      .catch((err) =>
        errorToast(
          err,
          <FormattedMessage
            id="shipment-quotes.components.requestApprovalDialog.couldNotSubmitApprovalRequest"
            defaultMessage="Could not submit approval request. Please try again later."
          />,
        ),
      )
      .finally(() => {
        setIsApproving(false);
        actions.updateBackgroundLoadingState(false);
      });
  };

  return (
    <Dialog
      size="md"
      title={intl.formatMessage({
        id: "shipment-quotes.components.transferApproval",
        defaultMessage: "Transfer Approval",
      })}
      show={isOpen}
      onHide={close}
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit(transferApproval)}>
        <p className="mb-1 text-secondary">
          <FormattedMessage
            id="shipment-quotes.components.transferApprovalDialog.identifyPossibleApprovers"
            defaultMessage="Please identify one or multiple possible approvers from the list below."
          />
        </p>
        <p className="text-secondary">
          <FormattedMessage
            id="shipment-quotes.components.transferApprovalDialog.note"
            defaultMessage="Please note, each identified approver receives a notification."
          />
        </p>
        {selectedApprovers.map((selectedApprover) => (
          <div className="d-flex justify-content-between py-3 px-4 mb-2 br-2  align-items-center" key={selectedApprover.id}>
            <p className="mb-0">{selectedApprover.label}</p>
            {!selectedApprover.is_invited && (
              <img
                src={Cross}
                alt="unselect approver"
                style={{ width: 12, height: 12, cursor: "pointer", opacity: 0.2 }}
                onClick={() => {
                  setApproverOptions([...approverOptions, selectedApprover]);
                  setSelectedApprovers(selectedApprovers.filter(({ id }) => id !== selectedApprover.id));
                }}
              />
            )}
          </div>
        ))}
        <div className="mt-4">
          <p className="font-weight-bold mb-2 required">
            <FormattedMessage
              id="shipment-quotes.components.transferApprovalDialog.requestApprovalFrom"
              defaultMessage="Request approval from"
            />
          </p>
          <Controller
            control={control}
            name="selected_approvers"
            render={({ fieldState }) => (
              <>
                <Typeahead
                  ref={typeaheadRef}
                  onChange={(event) => {
                    if (event[0]) {
                      clearErrors("selected_approvers");
                      setSelectedApprovers([...selectedApprovers, event[0]]);
                      setApproverOptions(approverOptions.filter((approver) => approver.id !== event[0].id));
                      typeaheadRef.current.clear();
                    }
                  }}
                  inputProps={{
                    style: { height: 40 },
                    name: "selected_approvers",
                    className: `${fieldState.error && "is-invalid"} `,
                  }}
                  options={approverOptions}
                  renderMenuItemChildren={({ label, email }, { text }) => (
                    <div className="p-2">
                      <div className="font-weight-bold font-12">
                        <Highlighter search={text}>{label}</Highlighter>
                      </div>
                      <div className="font-b3 mt-1 text-secondary">
                        <Highlighter search={text}>{email}</Highlighter>
                      </div>
                    </div>
                  )}
                  filterBy={["label", "email"]}
                  placeholder="Search users..."
                  id="selected_approvers"
                />
                {fieldState.error && <span className="text-danger">{fieldState.error.message}</span>}
              </>
            )}
          />
        </div>
        <div className="mt-4">
          <p className="font-weight-bold mb-2">
            <FormattedMessage id="shipment-quotes.components.transferApprovalDialog.comments" defaultMessage="Comments" />
          </p>
          <Form.Control as="textarea" rows={2} onChange={(e) => setComment(e.target.value)} className="" />
        </div>
        <Row className="mb-3 mt-4">
          <Col lg={12}>
            <div className="d-block border-grey border-top w-100" />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col lg={12} className="d-flex flex-row">
            <Button
              size="lg"
              className="btn-grey"
              label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
              onClick={close}
            />
            <Button
              className="ml-auto"
              label={intl.formatMessage({
                id: "shipment-quotes.components.transferApprovalDialog.manageApproval",
                defaultMessage: "Manage approval",
              })}
              variant="link"
              onClick={handleManageApprovalClick}
            />
            <Button
              size="lg"
              className="ml-3"
              variant="success"
              label={intl.formatMessage({
                id: "shipment-quotes.components.transferApprovalDialog.transferApprovalRequest",
                defaultMessage: "Transfer approval request",
              })}
              isLoading={isApproving}
              type="submit"
            />
          </Col>
        </Row>
      </form>
    </Dialog>
  );
};

TransferApprovalDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  openManageApprovalDialog: PropTypes.func.isRequired,
  setBookingPayload: PropTypes.func.isRequired,
  setShowBookQuoteDialog: PropTypes.func.isRequired,
  setBookingType: PropTypes.func.isRequired,
  quotationId: PropTypes.string.isRequired,
  shipmentId: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
};

export default TransferApprovalDialog;
